import { alpha, createTheme, Theme } from '@material-ui/core/styles'
import { BreakpointValues } from '@material-ui/core/styles/createBreakpoints'
import shadows from '@material-ui/core/styles/shadows'
import { color } from 'src/color/color'

declare module '@material-ui/core/styles/createBreakpoints' {
  interface BreakpointOverrides {
    xxl: true
    xl: true
    lg: true
    md: true
    sm: true
    xs: true
  }
}

const breakpoints: BreakpointValues = {
  xxl: 2040,
  xl: 1600,
  lg: 1200,
  md: 992,
  sm: 768,
  xs: 520,
}
const smMediaQuery = `@media (min-width: ${breakpoints.sm}px)`

export const theme: Theme = createTheme({
  palette: {
    primary: {
      main: color('blue'),
    },
    secondary: {
      main: color('orange'),
    },
    text: {
      primary: color('dark-gray'),
      secondary: color('light-gray'),
    },
    success: {
      light: color('bright-green'),
      main: color('positive-green'),
    },
    info: {
      light: color('bright-blue'),
      main: color('peaceful-blue'),
    },
    warning: {
      light: color('bright-yellow'),
      main: color('stressful-yellow'),
    },
    error: {
      light: color('bright-red'),
      main: color('dangerous-red'),
    },
    background: {
      default: color('white'),
    },
  },
  props: {
    MuiTypography: {
      variantMapping: {
        subtitle1: 'span',
        subtitle2: 'span',
        body1: 'span',
        body2: 'span',
      },
    },
  },
  typography: {
    button: {
      fontWeight: 700,
      fontSize: '1.6rem',
      letterSpacing: '0.07rem',
      lineHeight: '2rem',
      textTransform: 'none',
    },
    fontFamily: [
      '-apple-system', 'BlinkMacSystemFont', '"Segoe UI"', 'Roboto',
      '"Helvetica Neue"', 'Arial', 'sans-serif',
    ].join(','),
    htmlFontSize: 10,
    h1: {
      fontSize: '2.4rem',
      fontWeight: 700,
      color: color('blue'),

      [smMediaQuery]: {
        fontSize: '3.8rem',
      },
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      color: color('blue'),

      [smMediaQuery]: {
        fontSize: '2.4rem',
      },
    },
    h3: {
      fontSize: '1.8rem',
      fontWeight: 700,
      color: color('blue'),

      [smMediaQuery]: {
        fontSize: '2rem',
      },
    },
    h4: {
      fontSize: '1.6rem',
      fontWeight: 700,
      color: color('blue'),
    },
    h5: {
      fontSize: '1.2rem',
      fontWeight: 700,
      color: color('orange'),
    },
    body2: {
      fontSize: '1.2rem',
    },
  },
  overrides: {
    MuiIcon: {
      fontSizeLarge: {
        fontSize: '4.8rem',
      },
    },
    MuiSvgIcon: {
      colorPrimary: {
        color: color('blue'),
      },
    },
    MuiIconButton: {
      colorPrimary: {
        color: color('blue'),
      },
      sizeSmall: {
        fontSize: '1.6rem',
      },
    },
    MuiDialog: {
      paper: {
        borderRadius: '1rem',
      },
    },
    MuiTooltip: {
      tooltip: {
        fontSize: '1.2rem',
      },
    },
    MuiCard: {
      root: {
        borderRadius: '1rem',
        boxShadow: shadows[3],
      },
    },
    MuiButtonGroup: {
      groupedTextPrimary: {
        color: color('blue'),
        '&:hover': {
          background: color('light-blue-gray'),
        },
      },
    },
    MuiButton: {
      root: {
        borderRadius: 0,
      },
      contained: {
        height: '4.8rem',
        minWidth: '27rem',
        maxWidth: '100%',
        '&:hover': {
          color: color('white'),
        },
        '&:focus': {
          color: color('white'),
          outline: 'none',
        },
      },
      containedSizeSmall: {
        height: '3.4rem',
        minWidth: '16rem',
        maxWidth: '100%',
      },
      containedSecondary: {
        color: color('white'),
      },
      outlined: {
        height: '4.8rem',
      },
      outlinedSizeSmall: {
        height: '3.4rem',
        minWidth: '16rem',
        maxWidth: '100%',
      },
      text: {
        fontWeight: 400,
      },
      sizeSmall: {
        fontSize: '1.2rem',
      },
    },
    MuiInputBase: {
      input: {
        height: 'auto',
      },
    },
    MuiOutlinedInput: {
      root: {
        borderRadius: 0,
        '&$disabled': {
          background: alpha(color('black'), 0.12),
        },
      },
      input: {
        padding: '10.5px 14px',
      },
    },
    MuiFormLabel: {
      root: {
        lineHeight: 1.5,
        color: color('dark-gray'),
        '&.Mui-error': {
          color: color('dark-gray'),
        },
      },
    },
    MuiLinearProgress: {
      root: {
        borderRadius: 8,
        maskImage: 'radial-gradient(white, black)',
      },
      colorPrimary: {
        backgroundColor: color('gray'),
      },
      barColorPrimary: {
        background: `linear-gradient(90deg, ${color('midnight-blue')} 0%, ${color('peaceful-blue')} 100%)`,
      },
      buffer: {
        backgroundColor: color('gray'),
      },
      bar2Buffer: {
        backgroundColor: color('blue-gray'),
      },
      dashed: {
        display: 'none',
      },
    },
    MuiBadge: {
      colorSecondary: {
        color: color('white'),
      },
    },
    MuiTableHead: {
      root: {
        borderBottom: `1px solid ${color('gray')}`,
        borderTop: `1px solid ${color('gray')}`,
      },
    },
    MuiTableBody: {
      root: {
        borderBottom: `1px solid ${color('gray')}`,
      },
    },
    MuiTableRow: {
      root: {
        borderBottom: `1px solid ${color('gray')}`,
        '&:last-of-type': {
          borderBottom: 'none',
        },
      },
    },
    MuiTableCell: {
      root: {
        fontSize: '1.6rem',
        borderBottom: 'none',
      },
      head: {
        fontWeight: 700,
      },
    },
    MuiTab: {
      root: {
        padding: '24px 30px',
      },
    },
    MuiSwitch: {
      root: {
        overflow: 'unset',
      },
    },
    MuiStepLabel: {
      label: {
        '&.MuiStepLabel-active': {
          color: color('blue'),
        },
        '&.MuiStepLabel-completed': {
          color: color('positive-green'),
        },
      },
    },
    MuiStepConnector: {
      alternativeLabel: {
        top: '1.33em',
      },
    },
    MuiStepIcon: {
      root: {
        color: color('gray'),
        fontSize: '3.2rem',

        '&.MuiStepIcon-completed': {
          color: color('positive-green'),
        },
      },
    },
  },
  breakpoints: {
    values: breakpoints,
  },
})
